import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { useApolloClient } from '@apollo/client';
import { Item } from '@baemingo/order-status-events/dist/types';
import { useI18n, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { Station } from '~Types';
import { getCompanyData_Company_courseTypes } from '~Scenes/EventedKitchenDisplay/__queries__';
import { ItemWithCourseType } from '~Scenes/EventedKitchenDisplay';
import { EnrichedOrder } from '~Scenes/EventedKitchenDisplay/view';
import ItemName from './ItemName';
import { itemInBong } from '~Scenes/POS/Queries/__queries__';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';

const Wrapper = styled.div`
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  border-left: 1px solid var(--colorBlueGray1000);
  overflow-y: auto;
  background: var(--colorBlueGray1300);
`;

const PanelHeader = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--colorBlueGray1000);
`;

const PanelTitle = styled.div`
  color: var(--colorBlueGray500);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Section = styled.div<{ faded?: boolean }>`
  padding: 12px 20px;
  opacity: ${({ faded }) => (faded ? 0.5 : 1)};

  & + & {
    border-top: 1px solid var(--colorBlueGray1000);
  }
`;

const SectionTitle = styled.div`
  color: var(--colorBlueGray500);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  color: var(--colorBlueGray200);
  padding: 6px 0;
  gap: 12px;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &:last-child {
    border-bottom: none;
  }
`;

const Count = styled.div`
  min-width: 32px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  background: var(--colorBlueGray1000);
  border-radius: 6px;
  color: var(--colorWhite);
`;

const EmptyState = styled.div`
  color: var(--colorBlueGray500);
  font-size: 13px;
  padding: 8px 0;
`;

interface Props {
  allOrders: EnrichedOrder[];
  regularKDSOrders: EnrichedOrder[];
  upcomingOrders: EnrichedOrder[];
  courseTypes: getCompanyData_Company_courseTypes[];
  itemsWithCourseType: ItemWithCourseType[];
}

type Aggregated = {
  key: string;
  itemId: string;
  count: number;
};

const SummaryPanel: FC<Props> = ({ allOrders, regularKDSOrders, upcomingOrders }) => {
  const { i18n } = useI18n();
  const [storage, setStorage] = useStorage();
  const stations = useSelector((state) => state.stations || []);
  const client = useApolloClient();

  const selectedStation: Station | undefined = stations.find((s: Station) => s.id === storage.selectedKDSStation);

  // Try to read item name from Apollo cache for sorting
  const getItemName = (itemId: string): string => {
    const fragment = client.readFragment<itemInBong>({
      id: `Item${itemId}`,
      fragment: ITEM_FRAGMENT,
    });
    return fragment ? i18n(fragment.titleLang) : itemId;
  };

  const aggregate = (orders: EnrichedOrder[]) => {
    const map = new Map<string, Aggregated>();
    orders.forEach((order) => {
      order.order.items.forEach((item: Item) => {
        const key = [item.itemId, item.comment || '', item.variations.join('|'), item.removedVariations.join('|')].join('::');
        const existing = map.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(key, { key, itemId: item.itemId, count: 1 });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => getItemName(a.itemId).localeCompare(getItemName(b.itemId)));
  };

  const stationMatchesItem = (station: Station, item: Item) => {
    const noFilterMatch = station.items.length === 0 && station.categories.length === 0;
    const itemMatch = station.items.some((id) => id === item.itemId);
    const categoryMatch = station.categories.some(
      (category) => category.name === item.categoryId || category.id === item.categoryId,
    );
    return noFilterMatch || itemMatch || categoryMatch;
  };

  const otherStationIds = useMemo(() => {
    const ids = storage.KDSOtherStationsFilter || [];
    if (ids.length > 0) {
      return ids;
    }
    return stations.filter((s: Station) => s.id !== selectedStation?.id).map((s: Station) => s.id);
  }, [stations, selectedStation, storage.KDSOtherStationsFilter]);

  const otherStationsOrders = useMemo(() => {
    if (!selectedStation?.showAllItems) {
      return [] as EnrichedOrder[];
    }

    const relevantStations = stations.filter((s: Station) => otherStationIds.includes(s.id));
    return allOrders
      .map((order) => ({
        ...order,
        order: {
          ...order.order,
          items: order.order.items.filter((item: Item) => relevantStations.some((station: Station) => stationMatchesItem(station, item))),
        },
      }))
      .filter((order) => order.order.items.length > 0 && order.order.fired !== false);
  }, [allOrders, selectedStation, stations, otherStationIds]);

  const currentItems = useMemo(() => aggregate(regularKDSOrders), [regularKDSOrders]);
  const upcomingItems = useMemo(() => aggregate(upcomingOrders), [upcomingOrders]);
  const otherItems = useMemo(() => aggregate(otherStationsOrders), [otherStationsOrders]);

  const toggleStation = (stationId: string) => {
    const current = storage.KDSOtherStationsFilter || [];
    const next = current.includes(stationId)
      ? current.filter((id: string) => id !== stationId)
      : [...current, stationId];
    setStorage({ KDSOtherStationsFilter: next });
  };

  return (
    <Wrapper>
      <PanelHeader>
        <PanelTitle>{i18n('Settings.KDS.SummaryPanel') || 'Summary'}</PanelTitle>
      </PanelHeader>

      <Section>
        <SectionTitle>{i18n('KDS.CurrentOrders') || 'Current'}</SectionTitle>
        {currentItems.length === 0 && <EmptyState>{i18n('KDS.NoItems') || 'No items'}</EmptyState>}
        {currentItems.map((item) => (
          <Row key={item.key}>
            <Count>{item.count}</Count>
            <ItemName id={item.itemId} />
          </Row>
        ))}
      </Section>

      {selectedStation?.showAllItems && (
        <Section>
          <SectionTitle>{i18n('KDS.OtherStations')}</SectionTitle>
          {stations
            .filter((s: Station) => s.id !== selectedStation.id)
            .map((station: Station) => {
              const active = otherStationIds.includes(station.id);
              return (
                <Row key={station.id} onClick={() => toggleStation(station.id)} style={{ cursor: 'pointer', opacity: active ? 1 : 0.5 }}>
                  <span>{station.name}</span>
                  <Count style={{ background: active ? 'var(--colorBlueGray1000)' : 'transparent' }}>{active ? '✓' : ''}</Count>
                </Row>
              );
            })}
          {otherItems.map((item) => (
            <Row key={item.key}>
              <ItemName id={item.itemId} />
              <Count>{item.count}</Count>
            </Row>
          ))}
        </Section>
      )}

      {upcomingItems.length > 0 && (
        <Section faded>
          <SectionTitle>{i18n('KDS.Upcoming') || 'Upcoming'}</SectionTitle>
          {upcomingItems.map((item) => (
            <Row key={item.key}>
              <Count>{item.count}</Count>
              <ItemName id={item.itemId} />
            </Row>
          ))}
        </Section>
      )}
    </Wrapper>
  );
};

export default SummaryPanel;
